import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { browseCollections } from '@platform/client';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { SCREEN_GAP } from '../routes/PageHeader';

import { collectionQuery } from './collection';
import { CollectionHeader } from './CollectionHeader';
import { CollectionSettings } from './CollectionSettings';
import { CourseTheme } from './CourseTheme';
import { STATUS_COLOUR } from './status';

/**
 * One course, and the modules under it — GEN-58.
 *
 * Read through the same browse endpoint the list uses rather than a second one: a course
 * is found by its own id, and its modules are the collections whose `parentId` is that
 * id. Two queries, no new route, and the counts on screen are the counts the list showed.
 *
 * The heading, the title edit and archiving are `CollectionHeader`, shared with the path
 * screen. What is left here is the part that is about a course: what hangs below it.
 */
export function CourseDetail() {
  const { id = '' } = useParams<{ id: string }>();

  const course = useQuery(collectionQuery(id));

  // Asked for by parent rather than fetched and filtered here. Reading a page of a
  // hundred to find one row is a screen that works until the hundred-and-first course.
  const modules = useQuery({
    queryKey: ['course', id, 'modules'],
    queryFn: () =>
      browseCollections({
        query: { parentId: id, limit: 100 },
        throwOnError: true,
      }),
  });

  const row = course.data?.data;
  const held = modules.data?.data.collections ?? [];

  if (course.isPending) return <LinearProgress />;
  if (row === undefined)
    return <Alert severity="error">No course with that id.</Alert>;

  return (
    <Stack spacing={SCREEN_GAP}>
      <CollectionHeader row={row} />
      <CollectionSettings row={row} />
      <CourseTheme row={row} />

      <Typography variant="h6">Modules</Typography>

      {held.length === 0 ? (
        <Alert severity="info">
          This course holds no modules yet. A generation run puts them here.
        </Alert>
      ) : (
        <Stack spacing={1}>
          {held.map((one) => (
            <Stack
              key={one.id}
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center' }}
            >
              <Link to={`/library/modules/${one.id}`}>{one.title}</Link>
              <Chip
                size="small"
                color={STATUS_COLOUR[one.status]}
                label={one.status}
              />
              <Typography variant="caption" color="text.secondary">
                {String(one.lessons)} lesson(s)
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
